'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAdvisorSafe } from '@/contexts/AdvisorContext';
import { useStateDisclosure } from '@/hooks/useStateDisclosure';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';
import {
  DISCLOSURE_CATEGORIES,
  type DisclosureCategory,
} from '@/lib/disclosure/calculateProgress';
import {
  computeAccountCoverages,
  ACCOUNT_TYPE_TO_CATEGORY,
  type StatementPeriod,
  type AccountCoverage as GapAccountCoverage,
} from '@/hooks/useStatementGapDetection';
import {
  buildTaskRangeOverrides,
  type TaskRangeOverrides,
  type TaskRangeRow,
  type RequirementSource,
} from '@/lib/disclosure/taskRequirementOverrides';

export { ACCOUNT_TYPE_TO_CATEGORY };
export type { TaskRangeOverrides, TaskRangeRow, RequirementSource };

// =============================================================================
// TYPES
// =============================================================================

export interface AccountBreakdown {
  accountKey: string;
  coveredMonths: number;
  totalExpectedMonths: number;
  gapMonths: string[]; // ["March 2025", "July 2025"]
}

export interface CategoryCoverage {
  categoryId: string;
  displayName: string;
  requiredMonths: number;
  coveredMonths: number;
  documentCount: number;
  isComplete: boolean;
  isFinancialType: boolean;
  gaps: string[];
  accounts: AccountBreakdown[];
  /** Where the effective requirement comes from (#1314). Defaults to 'state'. */
  requirementSource?: RequirementSource;
  /** The widest attorney-assigned date range, when requirementSource is 'attorney'. */
  taskRange?: { start: string; end: string } | null;
}

export interface DisclosureCoverageResult {
  coverageMatrix: CategoryCoverage[];
  overallCompleteness: number; // 0-100
  missingCategories: string[];
  totalGaps: number;
  isLoading: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Maps uploaded_documents.document_type to disclosure categories
 */
const DOC_TYPE_TO_CATEGORY: Record<string, DisclosureCategory> = {
  bank_statement: 'bank_accounts',
  credit_card_statement: 'credit_cards',
  investment_statement: 'investments',
  retirement_statement: 'retirement',
  pay_stub: 'pay_stubs',
  payroll_summary: 'pay_stubs',
  tax_return: 'tax_returns',
  mortgage_statement: 'mortgages',
  property_deed: 'real_estate',
  appraisal: 'real_estate',
  vehicle_title: 'vehicles',
  loan_document: 'promissory_notes',
  insurance_document: 'pc_insurance',
  credit_report: 'credit_report',
  business_pnl: 'business',
  business_valuation: 'business',
  business_financial: 'business',
  schedule_c: 'business',
  form_1120s: 'business',
  spreadsheet: 'business',
  corporate_document: 'business',
  lease_agreement: 'leases',
  third_party_payment: 'third_party_payments',
  crypto_statement: 'crypto',
  life_insurance_policy: 'life_insurance',
  health_insurance_card: 'health_insurance',
  social_security_statement: 'ssa_statement',
  // ─── New mappings for FL 12.285 parity (#1256) ─────────────
  w2: 'w2_1099_k1',
  '1099': 'w2_1099_k1',
  k1: 'w2_1099_k1',
  business_tax_return: 'business_tax_returns',
  loan_application: 'loan_applications',
  financial_statement: 'loan_applications',
  income_statement: 'income_statement',
  premarital_agreement: 'premarital_agreements',
  court_order: 'court_support_orders',
  financial_affidavit: 'financial_affidavit',
};

/**
 * Default required months per category when state config is unavailable.
 * Per FL Rule 12.285 retention periods (#1257).
 */
export const DEFAULT_REQUIRED_MONTHS: Partial<Record<DisclosureCategory, number>> = {
  bank_accounts: 12,
  credit_cards: 24,
  investments: 12,
  retirement: 12,
  pay_stubs: 6,
  tax_returns: 36,       // 3 years
  mortgages: 12,
  // Added per #1257:
  leases: 36,            // 3 years
  real_estate: 36,       // 3 years
  loan_applications: 24, // 24 months
  promissory_notes: 24,  // 24 months
  life_insurance: 12,    // 12 months
  business_tax_returns: 36, // 3 years
  income_statement: 6,   // 6 months
};

/**
 * Financial categories that use period-based tracking via account_statement_periods.
 * These categories check actual month coverage rather than just document count.
 */
export const FINANCIAL_PERIOD_CATEGORIES: Set<DisclosureCategory> = new Set([
  'bank_accounts',
  'credit_cards',
  'investments',
  'retirement',
  'mortgages',
]);

// NOTE: ACCOUNT_TYPE_TO_CATEGORY moved to useStatementGapDetection (re-exported
// above) so gap detection can match attorney-assigned category windows to
// accounts without a circular import (#1314).

// =============================================================================
// PURE COMPUTATION (exported for testing)
// =============================================================================

interface DocumentRow {
  id: string;
  document_type: string | null;
  created_at: string;
  document_year: number | null;
}

/**
 * Build required months for a category.
 *
 * Resolution order (#1314):
 *   1. Per-case attorney task-range override (widest assigned range)
 *   2. state_disclosure_requirements rows
 *   3. State disclosure config document requirements
 *   4. DISCLOSURE_CATEGORIES defaults / DEFAULT_REQUIRED_MONTHS
 */
export function getRequiredMonths(
  categoryKey: DisclosureCategory,
  stateRequirements: Array<{
    category: string;
    required_periods: number | null;
    period_type: 'months' | 'years' | null;
  }>,
  documentRequirements: {
    bankStatementMonths: number;
    creditCardMonths: number;
    taxReturnYears: number;
    payStubMonths: number;
  } | null,
  taskOverrides?: TaskRangeOverrides,
): number {
  // Per-case attorney-assigned task ranges take precedence over state defaults
  const taskOverride = taskOverrides?.[categoryKey];
  if (taskOverride && taskOverride.months > 0) {
    return taskOverride.months;
  }

  // Check state-specific requirements first
  const stateReq = stateRequirements.find((r) => r.category === categoryKey);
  if (stateReq && stateReq.required_periods != null && stateReq.period_type) {
    return stateReq.period_type === 'years'
      ? stateReq.required_periods * 12
      : stateReq.required_periods;
  }

  // Use state disclosure config document requirements
  if (documentRequirements) {
    if (categoryKey === 'bank_accounts') return documentRequirements.bankStatementMonths;
    if (categoryKey === 'credit_cards') return documentRequirements.creditCardMonths;
    if (categoryKey === 'tax_returns') return documentRequirements.taxReturnYears * 12;
    if (categoryKey === 'pay_stubs') return documentRequirements.payStubMonths;
  }

  // Category-level requiredMonths from DISCLOSURE_CATEGORIES
  const catDef = DISCLOSURE_CATEGORIES[categoryKey];
  if ('requiredMonths' in catDef && catDef.requiredMonths) {
    return catDef.requiredMonths;
  }
  if ('requiredYears' in catDef && catDef.requiredYears) {
    return catDef.requiredYears * 12;
  }

  // Fallback defaults
  return DEFAULT_REQUIRED_MONTHS[categoryKey] ?? 0;
}

/**
 * Where a category's effective requirement comes from (#1314):
 * 'attorney' when an assigned task range overrides the default,
 * 'state' otherwise (FL Rule 12.285 / state config).
 */
export function getRequirementSource(
  categoryKey: DisclosureCategory,
  taskOverrides?: TaskRangeOverrides,
): RequirementSource {
  const taskOverride = taskOverrides?.[categoryKey];
  return taskOverride && taskOverride.months > 0 ? 'attorney' : 'state';
}

/** The attorney-assigned range to surface on a category, if any. */
function getTaskRange(
  categoryKey: DisclosureCategory,
  taskOverrides?: TaskRangeOverrides,
): { start: string; end: string } | null {
  const taskOverride = taskOverrides?.[categoryKey];
  if (!taskOverride || taskOverride.months <= 0) return null;
  return { start: taskOverride.startDate, end: taskOverride.endDate };
}

/**
 * Format a Date as a human-readable month string like "March 2025".
 */
export function formatMonthLabel(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Build per-account gap descriptions from gap detection account coverages.
 */
export function buildAccountBreakdowns(
  accountCoverages: GapAccountCoverage[],
  categoryKey: DisclosureCategory,
  requiredMonths: number,
): { accounts: AccountBreakdown[]; coveredMonths: number; gaps: string[] } {
  // Filter accounts belonging to this category
  const relevantAccounts = accountCoverages.filter((acct) => {
    const mappedCategory = ACCOUNT_TYPE_TO_CATEGORY[acct.accountType.toLowerCase()];
    return mappedCategory === categoryKey;
  });

  if (relevantAccounts.length === 0) {
    return {
      accounts: [],
      coveredMonths: 0,
      gaps: [`No ${DISCLOSURE_CATEGORIES[categoryKey].name.toLowerCase()} period data found`],
    };
  }

  const accounts: AccountBreakdown[] = [];
  const gaps: string[] = [];

  // Aggregate covered months across all accounts for the category.
  // Use the max covered months from any single account (not sum, since multiple
  // accounts for same category each need to individually cover the period).
  // Actually for disclosure purposes, each account's coverage is independent.
  // The category is complete when ALL accounts have full coverage.
  // But the spec says: coveredMonths vs requiredMonths is per-category.
  // We take a conservative approach: the category's covered months is the
  // minimum across accounts (all accounts must cover the required period).
  let minCoveredMonths = Infinity;

  for (const acct of relevantAccounts) {
    const gapMonths: string[] = [];
    for (const gap of acct.gaps) {
      // Walk each month in the gap range
      const cursor = new Date(gap.start.getFullYear(), gap.start.getMonth(), 1);
      const endMonth = new Date(gap.end.getFullYear(), gap.end.getMonth(), 1);
      while (cursor <= endMonth) {
        gapMonths.push(formatMonthLabel(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
      }
    }

    // Use requiredMonths as totalExpectedMonths if account doesn't span full range
    const totalExpected = Math.max(acct.totalExpectedMonths, requiredMonths);
    const covered = Math.min(acct.coveredMonths, requiredMonths);

    accounts.push({
      accountKey: acct.accountKey,
      coveredMonths: covered,
      totalExpectedMonths: totalExpected,
      gapMonths,
    });

    if (gapMonths.length > 0) {
      gaps.push(`${acct.accountKey}: Missing ${gapMonths.join(', ')}`);
    } else if (covered < requiredMonths) {
      // Account doesn't span the full required period (no internal gaps, but range is too short)
      const shortfall = requiredMonths - covered;
      gaps.push(
        `${acct.accountKey}: ${shortfall} more month${shortfall !== 1 ? 's' : ''} of coverage needed`
      );
    }

    if (covered < minCoveredMonths) {
      minCoveredMonths = covered;
    }
  }

  const coveredMonths = minCoveredMonths === Infinity ? 0 : minCoveredMonths;

  return { accounts, coveredMonths, gaps };
}

/**
 * Compute disclosure coverage using document counts only (legacy / non-HHD mode).
 */
export function computeDocCountCoverage(
  documents: DocumentRow[],
  stateRequirements: Array<{
    category: string;
    required_periods: number | null;
    period_type: 'months' | 'years' | null;
  }>,
  documentRequirements: {
    bankStatementMonths: number;
    creditCardMonths: number;
    taxReturnYears: number;
    payStubMonths: number;
  } | null,
  taskOverrides?: TaskRangeOverrides,
): Omit<DisclosureCoverageResult, 'isLoading'> {
  // Count documents per disclosure category
  const docCountsByCategory: Partial<Record<DisclosureCategory, number>> = {};
  for (const doc of documents) {
    if (doc.document_type) {
      const category = DOC_TYPE_TO_CATEGORY[doc.document_type];
      if (category) {
        docCountsByCategory[category] = (docCountsByCategory[category] ?? 0) + 1;
      }
    }
  }

  const coverageMatrix: CategoryCoverage[] = [];
  let completedCount = 0;
  let totalWithRequirements = 0;
  const missingCategories: string[] = [];
  let totalGaps = 0;

  for (const [key, catDef] of Object.entries(DISCLOSURE_CATEGORIES)) {
    const categoryKey = key as DisclosureCategory;
    const requiredMonths = getRequiredMonths(
      categoryKey,
      stateRequirements,
      documentRequirements,
      taskOverrides,
    );

    if (requiredMonths === 0) continue;

    totalWithRequirements++;

    const documentCount = docCountsByCategory[categoryKey] ?? 0;
    // Yearly categories: each document covers 12 months (e.g., tax returns, business tax returns)
    const isYearlyCategory = 'requiredYears' in catDef && catDef.requiredYears;
    const monthsPerDoc = isYearlyCategory ? 12 : 1;
    const coveredMonths = Math.min(documentCount * monthsPerDoc, requiredMonths);
    const isComplete = coveredMonths >= requiredMonths;

    const gaps: string[] = [];
    if (!isComplete) {
      const remaining = requiredMonths - coveredMonths;
      if (documentCount === 0) {
        gaps.push(`No ${catDef.name.toLowerCase()} uploaded yet`);
      } else {
        gaps.push(
          `${remaining} more month${remaining !== 1 ? 's' : ''} of coverage needed`
        );
      }
      missingCategories.push(catDef.name);
      totalGaps += remaining;
    } else {
      completedCount++;
    }

    coverageMatrix.push({
      categoryId: categoryKey,
      displayName: catDef.name,
      requiredMonths,
      documentCount,
      coveredMonths,
      isComplete,
      isFinancialType: FINANCIAL_PERIOD_CATEGORIES.has(categoryKey),
      gaps,
      accounts: [],
      requirementSource: getRequirementSource(categoryKey, taskOverrides),
      taskRange: getTaskRange(categoryKey, taskOverrides),
    });
  }

  const overallCompleteness =
    totalWithRequirements > 0
      ? Math.round((completedCount / totalWithRequirements) * 100)
      : 0;

  return {
    coverageMatrix,
    overallCompleteness,
    missingCategories,
    totalGaps,
  };
}

/**
 * Compute disclosure coverage using period-based tracking for financial categories
 * and document counts for non-financial categories (HHD pilot mode).
 */
export function computePeriodCoverage(
  documents: DocumentRow[],
  statementPeriods: StatementPeriod[],
  stateRequirements: Array<{
    category: string;
    required_periods: number | null;
    period_type: 'months' | 'years' | null;
  }>,
  documentRequirements: {
    bankStatementMonths: number;
    creditCardMonths: number;
    taxReturnYears: number;
    payStubMonths: number;
  } | null,
  taskOverrides?: TaskRangeOverrides,
): Omit<DisclosureCoverageResult, 'isLoading'> {
  // Count documents per disclosure category
  const docCountsByCategory: Partial<Record<DisclosureCategory, number>> = {};
  for (const doc of documents) {
    if (doc.document_type) {
      const category = DOC_TYPE_TO_CATEGORY[doc.document_type];
      if (category) {
        docCountsByCategory[category] = (docCountsByCategory[category] ?? 0) + 1;
      }
    }
  }

  // Compute account coverages from statement periods
  const accountCoverages = computeAccountCoverages(statementPeriods);

  const coverageMatrix: CategoryCoverage[] = [];
  let completedCount = 0;
  let totalWithRequirements = 0;
  const missingCategories: string[] = [];
  let totalGaps = 0;

  for (const [key, catDef] of Object.entries(DISCLOSURE_CATEGORIES)) {
    const categoryKey = key as DisclosureCategory;
    const requiredMonths = getRequiredMonths(
      categoryKey,
      stateRequirements,
      documentRequirements,
      taskOverrides,
    );
    const requirementSource = getRequirementSource(categoryKey, taskOverrides);
    const taskRange = getTaskRange(categoryKey, taskOverrides);
    const isFinancialType = FINANCIAL_PERIOD_CATEGORIES.has(categoryKey);
    const documentCount = docCountsByCategory[categoryKey] ?? 0;

    // ALL categories count toward the denominator — Phil needs 20/20
    totalWithRequirements++;

    // Categories with no time requirement: complete when at least 1 document exists
    if (requiredMonths === 0 && !isFinancialType) {
      const isComplete = documentCount >= 1;
      if (!isComplete) {
        missingCategories.push(catDef.name);
      } else {
        completedCount++;
      }

      coverageMatrix.push({
        categoryId: categoryKey,
        displayName: catDef.name,
        requiredMonths: 0,
        documentCount,
        coveredMonths: 0,
        isComplete,
        isFinancialType: false,
        gaps: isComplete ? [] : [`Add ${catDef.name.toLowerCase()} information`],
        accounts: [],
        requirementSource,
        taskRange,
      });
      continue;
    }

    if (isFinancialType) {
      // Period-based tracking for financial categories
      const {
        accounts,
        coveredMonths: periodCovered,
        gaps: periodGaps,
      } = buildAccountBreakdowns(accountCoverages, categoryKey, requiredMonths);

      // #1796: a document with no readable statement period is a document, not a month. This used to fall
      // back to counting documents whenever an account had no period rows — twelve copies of July were
      // "compliant" under a perjury declaration. Coverage comes from account_statement_periods, always.
      const coveredMonths = periodCovered;
      const isComplete = accounts.length > 0 && coveredMonths >= requiredMonths;

      const gaps: string[] =
        accounts.length > 0
          ? periodGaps
          : documentCount === 0
            ? [`No ${catDef.name.toLowerCase()} uploaded yet`]
            : [`${documentCount} ${catDef.name.toLowerCase()} document${documentCount !== 1 ? 's' : ''} uploaded, but no statement period has been read from ${documentCount !== 1 ? 'them' : 'it'} yet — months are counted from statement periods, not from files`];

      if (!isComplete) {
        missingCategories.push(catDef.name);
        // Count gap months from accounts
        const accountGapMonths = accounts.reduce((sum, a) => sum + a.gapMonths.length, 0);
        totalGaps += accountGapMonths > 0 ? accountGapMonths : (requiredMonths - coveredMonths);
      } else {
        completedCount++;
      }

      coverageMatrix.push({
        categoryId: categoryKey,
        displayName: catDef.name,
        requiredMonths,
        documentCount,
        coveredMonths,
        isComplete,
        isFinancialType: true,
        gaps: isComplete ? [] : gaps,
        accounts,
        requirementSource,
        taskRange,
      });
    } else {
      // Non-financial: document count based
      // Yearly categories: each document covers 12 months
      const isYearlyCategory = 'requiredYears' in catDef && catDef.requiredYears;
      const monthsPerDoc = isYearlyCategory ? 12 : 1;
      const coveredMonths = Math.min(documentCount * monthsPerDoc, requiredMonths);
      const isComplete = coveredMonths >= requiredMonths;

      const gaps: string[] = [];
      if (!isComplete) {
        const remaining = requiredMonths - coveredMonths;
        if (documentCount === 0) {
          gaps.push(`No ${catDef.name.toLowerCase()} uploaded yet`);
        } else {
          gaps.push(
            `${remaining} more month${remaining !== 1 ? 's' : ''} of coverage needed`
          );
        }
        missingCategories.push(catDef.name);
        totalGaps += remaining;
      } else {
        completedCount++;
      }

      coverageMatrix.push({
        categoryId: categoryKey,
        displayName: catDef.name,
        requiredMonths,
        documentCount,
        coveredMonths,
        isComplete,
        isFinancialType: false,
        gaps,
        accounts: [],
        requirementSource,
        taskRange,
      });
    }
  }

  const overallCompleteness =
    totalWithRequirements > 0
      ? Math.round((completedCount / totalWithRequirements) * 100)
      : 0;

  return {
    coverageMatrix,
    overallCompleteness,
    missingCategories,
    totalGaps,
  };
}

// =============================================================================
// TASK RANGE FETCHING (shared with useTaskRangeAutoProgress)
// =============================================================================

/** Query key for attorney-assigned task ranges (#1314). */
export function taskRangeQueryKey(userId: string | null | undefined) {
  return ['disclosure-task-ranges', userId] as const;
}

/** Fetch document_tasks rows that carry an assigned date range. */
export async function fetchTaskRangeRows(userId: string): Promise<TaskRangeRow[]> {
  const { data, error } = await supabase
    .from('document_tasks')
    .select('id, status, account_identifier, document_category_id, date_range_start, date_range_end')
    .eq('user_id', userId)
    .not('date_range_start', 'is', null)
    .not('date_range_end', 'is', null);

  if (error) {
    trackSupabaseError('select', 'document_tasks', error);
    throw error;
  }

  return (data ?? []) as TaskRangeRow[];
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Computes disclosure coverage by comparing uploaded documents and statement
 * period data against state-specific disclosure requirements.
 *
 * When the HHD pilot feature flag is enabled, financial categories use
 * period-based tracking from account_statement_periods for precise gap
 * detection. When disabled, falls back to document-count approximation.
 *
 * Issue: #1194 (fix completeness computation), #1142, #1147
 */
export function useDisclosureCoverage(): DisclosureCoverageResult {
  const { user } = useAuth();
  const { selectedClientId, isAdvisorMode } = useAdvisorSafe();
  const { disclosureContext, requirements, isLoading: isLoadingState } = useStateDisclosure();
  const userId = isAdvisorMode ? selectedClientId : user?.id;

  // Fetch all completed uploaded documents for this user
  const { data: documents, isLoading: isLoadingDocs } = useQuery({
    queryKey: ['disclosure-coverage-documents', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('uploaded_documents')
        .select('id, document_type, created_at, document_year')
        .eq('user_id', userId)
        .eq('processing_status', 'complete')
        .neq('data_source', 'email');

      if (error) {
        trackSupabaseError('select', 'uploaded_documents', error);
        throw error;
      }

      return data ?? [];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
  });

  // Fetch attorney-assigned task ranges — per-case requirement overrides (#1314)
  const { data: taskRangeRows, isLoading: isLoadingTaskRanges } = useQuery({
    queryKey: taskRangeQueryKey(userId),
    queryFn: () => fetchTaskRangeRows(userId!),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
  });

  // Statement periods: the only thing a month is counted from (#1796 — no longer behind the pilot flag).
  const { data: statementPeriods, isLoading: isLoadingPeriods } = useQuery({
    queryKey: ['statement-periods', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('account_statement_periods')
        .select('*')
        .eq('user_id', userId);

      if (error) {
        trackSupabaseError('select', 'account_statement_periods', error);
        throw error;
      }
      return (data ?? []) as StatementPeriod[];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
  });

  const coverageResult = useMemo((): Omit<DisclosureCoverageResult, 'isLoading'> => {
    if (!documents) {
      return {
        coverageMatrix: [],
        overallCompleteness: 0,
        missingCategories: [],
        totalGaps: 0,
      };
    }

    const docRequirements = disclosureContext.documentRequirements ?? null;
    const taskOverrides = buildTaskRangeOverrides(taskRangeRows ?? []);

    // #1796: period coverage for everyone. `computeDocCountCoverage` stays exported for callers that want a
    // document tally, but a month is never ticked by it here.
    return computePeriodCoverage(
      documents,
      statementPeriods ?? [],
      requirements,
      docRequirements,
      taskOverrides,
    );
  }, [documents, statementPeriods, taskRangeRows, requirements, disclosureContext]);

  return {
    ...coverageResult,
    isLoading:
      isLoadingState ||
      isLoadingDocs ||
      isLoadingTaskRanges ||
      isLoadingPeriods,
  };
}

export default useDisclosureCoverage;
